/**
 * Mesas abertas na mesma janela (docs/spec/open-rooms.md): a da tela e as de fundo, na ordem
 * dos botões da barra lateral, com as mensagens não lidas de cada uma e o relógio da sessão.
 */
import { nextClock, type RoomClock } from './clock/clockModel.js';
import { sessionRules, type GameSession, type Room } from '../shared.js';

export type OpenRoom = {
  roomId: string;
  name: string;
  /** mensagens e rolagens de outras pessoas desde que a mesa saiu da tela */
  unread: number;
  /** entre as não lidas há uma menção ao meu nome ou uma mensagem privada */
  mention: boolean;
  /** o relógio da sessão da mesa (docs/spec/session-clock.md); null até a mesa responder */
  clock: RoomClock | null;
  /** a mesa mostra o anel do relógio no botão (o registro e o relógio da barra ligados) */
  ring: boolean;
  /** a marca da mesa (Configurações da mesa > Perfil), no lugar das iniciais (BRAZ-33); null sem marca */
  logoUrl: string | null;
};

/** Cada mesa de fundo é uma conexão aberta: um teto evita dezenas delas. */
export const MAX_OPEN_ROOMS = 8;

/** Põe a mesa no fim da lista (se já estiver, só atualiza o nome conhecido). */
export function withRoom(list: readonly OpenRoom[], roomId: string, name?: string): OpenRoom[] {
  if (list.some((room) => room.roomId === roomId)) return name ? renamed(list, roomId, name) : [...list];
  return [...list, { roomId, name: name ?? '', unread: 0, mention: false, clock: null, ring: true, logoUrl: null }];
}

/** O relógio da mesa com a sessão que chegou (`live`: agora, e não ao entrar). */
export function withClock(list: readonly OpenRoom[], roomId: string, session: GameSession | null, live: boolean): OpenRoom[] {
  return list.map((room) => (room.roomId === roomId ? { ...room, clock: nextClock(room.clock, session, live) } : room));
}

export function withoutRoom(list: readonly OpenRoom[], roomId: string): OpenRoom[] {
  return list.filter((room) => room.roomId !== roomId);
}

export function renamed(list: readonly OpenRoom[], roomId: string, name: string): OpenRoom[] {
  return list.map((room) => (room.roomId === roomId && room.name !== name ? { ...room, name } : room));
}

/** O nome, o anel e a marca que a mesa mandou (no `ready` e no `room.updated`). */
export function described(list: readonly OpenRoom[], room: Pick<Room, 'id' | 'name' | 'gameRules'> & { logoUrl?: string | null }): OpenRoom[] {
  const rules = sessionRules(room);
  const ring = rules.record && rules.barClock;
  const logoUrl = room.logoUrl ?? null;
  return list.map((open) =>
    open.roomId === room.id && (open.name !== room.name || open.ring !== ring || open.logoUrl !== logoUrl) ? { ...open, name: room.name, ring, logoUrl } : open,
  );
}

/** Mais uma não lida (`mention`: menção ou privada, que destacam o botão). */
export function countUnread(list: readonly OpenRoom[], roomId: string, mention: boolean): OpenRoom[] {
  return list.map((room) => (room.roomId === roomId ? { ...room, unread: room.unread + 1, mention: room.mention || mention } : room));
}

export function markRead(list: readonly OpenRoom[], roomId: string): OpenRoom[] {
  return list.map((room) => (room.roomId === roomId && (room.unread || room.mention) ? { ...room, unread: 0, mention: false } : room));
}

/** Iniciais do nome da mesa, como no botão da barra lateral ("Mesa de Testes" → "MT"). */
export function roomInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const second = parts.length > 1 ? parts[parts.length - 1]![0] : (parts[0]?.[1] ?? '');
  return (first + second).toUpperCase();
}

/** O texto cita algum dos nomes com @ (sem diferenciar maiúsculas). */
export function mentions(text: string, names: readonly (string | null | undefined)[]): boolean {
  const lower = text.toLowerCase();
  return names.some((name) => !!name && lower.includes(`@${name.toLowerCase()}`));
}
